import { startTransition, useEffect, useState } from "react";
import { getDb, loadFirestoreModule } from "@/lib/firebase/db";
import { firebaseIdle } from "@/lib/firebase/app";
import { EMPTY_POPULARITY, type Popularity } from "@/lib/relevance";
import { bewaarStartwaarde, leesStartwaarde, zelfdeWaarde } from "@/lib/start-cache";

/**
 * Verkoopsnelheid per artikel uit Firestore `settings/popularity` (nachtelijk berekend door de
 * `aggregatePopularity`-functie: verkochte aantallen per `productId`/slug over de laatste 30
 * dagen). Eén gecachte read per sessie; gebruikt als zwaarste signaal in de "Relevantie"-sortering.
 * Ontbreekt het doc of mislukt de read (bv. App Check op localhost) → lege map (geen effect).
 */
export { relevanceScore, coPurchasedSlugs, coCartSlugs, type Popularity, type RelevanceInput } from "@/lib/relevance";

const EMPTY = EMPTY_POPULARITY;
// Begint met wat deze browser de vorige keer las (zie start-cache.ts): anders schoven de kaarten
// in de "Relevantie"-volgorde bij elk bezoek een seconde na het laden nog van plek.
let cached: Popularity | null = leesStartwaarde<Popularity>("popularity");
/** Is `cached` al de live waarde van dít bezoek? */
let live = false;
const subscribers = new Set<(p: Popularity) => void>();
// useTileRelevance() monteert deze hook twee keer in SiteLayout (hoofdnav + strip-nav). Zonder
// deze in-flight-gate zag elke instantie `cached === null` en startte elk zíjn EIGEN `readDoc()`.
let pending: Promise<Popularity | null> | null = null;

function numMap(raw: unknown): Record<string, number> {
  if (!raw || typeof raw !== "object") return {};
  const out: Record<string, number> = {};
  for (const [slug, n] of Object.entries(raw as Record<string, unknown>)) {
    const v = Number(n);
    if (slug && v > 0) out[slug] = v;
  }
  return out;
}

function coMap(raw: unknown): Record<string, Record<string, number>> {
  if (!raw || typeof raw !== "object") return {};
  const out: Record<string, Record<string, number>> = {};
  for (const [slug, neighbors] of Object.entries(raw as Record<string, unknown>)) {
    const m = numMap(neighbors);
    if (slug && Object.keys(m).length) out[slug] = m;
  }
  return out;
}

async function readDoc(): Promise<Popularity | null> {
  try {
    const [{ doc, getDoc }, db] = await Promise.all([loadFirestoreModule(), getDb()]);
    const s = await getDoc(doc(db, "settings", "popularity"));
    if (!s.exists()) return EMPTY;
    const d = s.data() as { units?: unknown; views?: unknown; coPurchase?: unknown; coCart?: unknown };
    return { units: numMap(d.units), views: numMap(d.views), coPurchase: coMap(d.coPurchase), coCart: coMap(d.coCart) };
  } catch {
    return null;
  }
}

function loadOnce(): Promise<Popularity | null> {
  if (live && cached != null) return Promise.resolve(cached);
  if (!pending) {
    pending = firebaseIdle().then(readDoc).then((c) => {
      pending = null;
      if (c != null) {
        live = true;
        bewaarStartwaarde("popularity", c);
        if (zelfdeWaarde(c, cached)) return cached;
        cached = c;
        // Meerdere secties wachten hierop tegelijk — zonder startTransition zet React al hun
        // re-renders in ÉÉN synchrone, niet-onderbreekbare taak (zie catalog-overrides.ts).
        startTransition(() => { subscribers.forEach((fn) => fn(c)); });
      }
      return c;
    });
  }
  return pending;
}

/** Live verkoopsnelheid-map; leeg tot de Firestore-read klaar is. */
export function usePopularity(): Popularity {
  const [v, setV] = useState<Popularity>(cached ?? EMPTY);
  useEffect(() => {
    const onChange = (p: Popularity): void => setV(p);
    subscribers.add(onChange);
    if (cached != null) setV(cached);
    if (!live) void loadOnce();
    return () => { subscribers.delete(onChange); };
  }, []);
  return v;
}
